<!-- Based on these implementations:
    - https://github.com/microsoft/sonder-ui/tree/master/src/components/select
    - https://www.w3.org/WAI/ARIA/apg/patterns/combobox/examples/combobox-select-only/
-->

<template>
    <div ref="wrapperRef" class="shrink-0">
        <label :id="labelId" class="sr-only">{{ props.label }}</label>
        <Tooltip :is-disabled="!hasTooltip">
            <template #trigger="tooltipProps">
                <div
                    ref="currentValueRef"
                    class="group/badge-dropdown inline-flex shrink-0 items-center rounded-sm font-medium ring-1 ring-inset bg-(--color-background) text-(--color-text) focus-visible:outline-offset-1 focus-visible:outline-2 focus-visible:z-20"
                    :style="colorStyle"
                    :class="[stateClasses, focusClasses, isDisabled ? 'cursor-not-allowed' : 'cursor-pointer']"
                    :aria-describedby="tooltipProps['aria-labelledby'] ? tooltipProps['aria-labelledby'] : undefined"
                    :popovertarget="tooltipProps['popovertarget'] ? tooltipProps.popovertarget : undefined"
                    :aria-disabled="isDisabled"
                    :aria-activedescendant="idFocusedOption"
                    aria-autocomplete="none"
                    :aria-controls="listboxId"
                    :aria-expanded="isOpen"
                    aria-haspopup="listbox"
                    :aria-labelledby="labelId"
                    :role="isRetry ? 'button' : 'combobox'"
                    tabindex="0"
                    @mouseover="!isDisabled ? (isHovered = true) : null"
                    @mouseout="!isDisabled ? (isHovered = false) : null"
                    @keydown="keydownTrigger"
                    @click="clickTrigger"
                >
                    <div class="flex flex-row gap-2 items-center rounded-l-sm" :class="[sizeClasses]">
                        <WarningIcon v-if="isRetry" class="text-(--color-error)" :class="iconSizeClass" />

                        {{ props.currentValue.label }}
                    </div>

                    <div
                        class="border-y border-r self-stretch flex flex-row items-center pr-1.75 gap-1.75 rounded-r-sm"
                        :class="
                            isDisabled
                                ? 'bg-(--color-disabled-background) text-(--color-disabled-text) border-(--color-disabled-ring)/10'
                                : 'border-transparent'
                        "
                    >
                        <div class="w-px" :class="[separatorHeightClass, separatorColorClasses]" />

                        <TriangleIcon
                            v-if="isDefault || isDisabled"
                            class="rounded-xs transition-transform"
                            :class="[iconSizeClass, isOpen && isDefault ? '-rotate-90' : 'rotate-90']"
                        />
                        <PendingIcon v-if="isLoading" class="rounded-xs animate-spin" :class="iconSizeClass" />
                        <RefreshIcon v-if="isRetry" class="rounded-xs" :class="iconSizeClass" />
                    </div>
                </div>
            </template>

            <template #text>
                <div class="max-w-md text-left">
                    <div v-if="props.tooltip">
                        {{ props.tooltip }}
                    </div>
                    <div
                        v-if="props.currentValue.explanation"
                        :class="{ 'mt-1 border-t border-gray-200 pt-1 text-gray-600': props.tooltip }"
                    >
                        {{ props.currentValue.explanation }}
                    </div>
                </div>
            </template>
        </Tooltip>

        <div
            :id="listboxId"
            ref="valuesPanelRef"
            class="bg-gray-50 space-y-0.5 shadow-sm rounded-sm z-20"
            :class="[{ 'py-0.5': isOpen }]"
            :style="floatingStyles"
            role="listbox"
            :aria-labelledby="labelId"
            tabindex="-1"
        >
            <div
                v-for="(option, index) in options"
                v-if="canBeOpened && isOpen"
                :id="`${optionId}-${index}`"
                :key="option.id"
                class="flex flex-row items-center pl-2.5 pr-3 py-1 focus-visible:outline-offset-1 focus-visible:outline-2 focus-visible:outline-sky-700 focus-visible:rounded-sm outline-hidden"
                :class="[
                    { 'bg-sky-700 cursor-pointer': option.isFocused },
                    option.isFocused ? 'text-gray-50' : 'text-gray-700',
                ]"
                role="option"
                :aria-selected="option.isSelected"
                @mouseover="focusedIndex = index"
                @mouseout="focusedIndex = -1"
                @focus="focusedIndex = index"
                @focusout="focusedIndex = -1"
                @click="handleOptionClick(option.id)"
            >
                <CheckIcon v-if="option.isSelected" class="mr-2 size-4" />
                <div v-else class="size-4 mr-2" />

                <div class="flex flex-col">
                    {{ option.label }}

                    <span class="text-xs" :class="[option.isFocused ? 'text-gray-300' : 'text-gray-500']">
                        {{ option.explanation }}
                    </span>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { onClickOutside } from '@vueuse/core';
import { nanoid } from 'nanoid';
import { computed, provide, ref, watchEffect } from 'vue';
import CheckIcon from '../Icon/CheckIcon.vue';
import PendingIcon from '../Icon/PendingIcon.vue';
import RefreshIcon from '../Icon/RefreshIcon.vue';
import TriangleIcon from '../Icon/TriangleIcon.vue';
import WarningIcon from '../Icon/WarningIcon.vue';
import Tooltip from '../Tooltip/Tooltip.vue';
import { triggerHideTooltipKey } from '../Tooltip/injectionKeys';
import { ComponentSize } from '../sharedTypes';

export type BadgeDropDownStatus = 'Default' | 'Disabled' | 'Loading' | 'Retry';
export type BadgeDropDownVariant = 'Green' | 'Yellow' | 'Red' | 'Gray' | 'Purple';

export type BadgeDropDownValue = {
    id: string;
    label: string;
    variant: BadgeDropDownVariant;
    explanation?: string;
};

type BadgeDropDownProps = {
    label: string;
    currentValue: BadgeDropDownValue;
    values: (BadgeDropDownValue & { isSelected: boolean })[];
    status: BadgeDropDownStatus;
    size: ComponentSize;
    tooltip?: string;
};

const props = defineProps<BadgeDropDownProps>();

const emit = defineEmits<{
    (e: 'changeValue', value: string): void;
    (e: 'retry'): void;
}>();

const isDefault = computed(() => props.status === 'Default');
const isDisabled = computed(() => props.status === 'Disabled');
const isLoading = computed(() => props.status === 'Loading');
const isRetry = computed(() => props.status === 'Retry');
const hasTooltip = computed(() => Boolean(props.tooltip) || Boolean(props.currentValue.explanation));
const canBeOpened = computed(() => !isDisabled.value && !isRetry.value);

const options = ref<(BadgeDropDownValue & { isSelected: boolean; isFocused: boolean })[]>([]);
watchEffect(() => {
    options.value = props.values.map((value) => {
        return {
            ...value,
            isFocused: false,
        };
    });
});
const idFocusedOption = computed(() => {
    const index = options.value.findIndex((option) => option.isFocused);

    if (index === -1) {
        return undefined;
    }

    return `${optionId}-${index}`;
});

const focusedIndex = ref(-1);
watchEffect(() => {
    focusOption(focusedIndex.value);
});

function focusOption(focusIndex: number) {
    options.value = options.value.map((option, optionIndex) => {
        return {
            ...option,
            isFocused: focusIndex === optionIndex,
        };
    });
}

function focusFirstOption() {
    focusedIndex.value = 0;
}

function focusLastOption() {
    focusedIndex.value = options.value.length - 1;
}

function focusPreviousOption() {
    focusedIndex.value = Math.max(focusedIndex.value - 1, 0);
}

function focusNextOption() {
    focusedIndex.value = Math.min(focusedIndex.value + 1, options.value.length - 1);
}

function handleKeyDown(event: KeyboardEvent) {
    // When collapsed
    if (!isOpen.value) {
        switch (event.key) {
            case 'Enter':
            case ' ':
            case 'ArrowDown':
                isOpen.value = true;
                focusFirstOption();
                event.preventDefault();
                return;

            case 'ArrowUp':
                isOpen.value = true;
                focusLastOption();
                event.preventDefault();
                return;
        }
    }

    // When expanded
    if (isOpen.value) {
        switch (event.key) {
            case 'Enter':
            case ' ':
                emit('changeValue', options.value[focusedIndex.value].id);
                isOpen.value = false;
                event.preventDefault();
                return;

            case 'ArrowUp':
                focusPreviousOption();
                event.preventDefault();
                return;

            case 'ArrowDown':
                focusNextOption();
                event.preventDefault();
                return;

            case 'PageUp':
                focusFirstOption();
                event.preventDefault();
                return;

            case 'PageDown':
                focusLastOption();
                event.preventDefault();
                return;

            case 'Escape':
                focusOption(-1);
                isOpen.value = false;
                event.preventDefault();
                return;

            case 'Tab':
                isOpen.value = false;
                return;
        }

        return;
    }
}

function handleOptionClick(id: string) {
    emit('changeValue', id);
    isOpen.value = false;
}

const labelId = nanoid();
const listboxId = nanoid();
const optionId = nanoid();

const isOpen = ref(false);
const isHovered = ref(false);

const colorStyle = computed(() => {
    switch (props.currentValue.variant) {
        case 'Green':
            return {
                '--color-background': 'var(--color-green-50)',
                '--color-background-active': 'var(--color-green-100)',
                '--color-text': 'var(--color-green-700)',
                '--color-ring': 'var(--color-green-700)',
                '--color-ring-active': 'var(--color-green-900)',
                '--color-outline': 'var(--color-green-700)',
                '--color-separator': 'var(--color-green-600)',
                '--color-separator-active': 'var(--color-green-700)',
                '--color-error': 'var(--color-red-700)',
                '--color-disabled-background': 'var(--color-gray-100)',
                '--color-disabled-text': 'var(--color-gray-400)',
                '--color-disabled-ring': 'var(--color-gray-700)',
                '--color-disabled-separator': 'var(--color-gray-500)',
                '--color-disabled-outline': 'var(--color-gray-500)',
            };

        case 'Yellow':
            return {
                '--color-background': 'var(--color-yellow-50)',
                '--color-background-active': 'var(--color-yellow-100)',
                '--color-text': 'var(--color-yellow-700)',
                '--color-ring': 'var(--color-yellow-700)',
                '--color-ring-active': 'var(--color-yellow-900)',
                '--color-outline': 'var(--color-yellow-700)',
                '--color-separator': 'var(--color-yellow-600)',
                '--color-separator-active': 'var(--color-yellow-700)',
                '--color-error': 'var(--color-red-700)',
                '--color-disabled-background': 'var(--color-gray-100)',
                '--color-disabled-text': 'var(--color-gray-400)',
                '--color-disabled-ring': 'var(--color-gray-700)',
                '--color-disabled-separator': 'var(--color-gray-500)',
                '--color-disabled-outline': 'var(--color-gray-500)',
            };

        case 'Red':
            return {
                '--color-background': 'var(--color-red-50)',
                '--color-background-active': 'var(--color-red-100)',
                '--color-text': 'var(--color-red-700)',
                '--color-ring': 'var(--color-red-700)',
                '--color-ring-active': 'var(--color-red-900)',
                '--color-outline': 'var(--color-red-700)',
                '--color-separator': 'var(--color-red-600)',
                '--color-separator-active': 'var(--color-red-700)',
                '--color-error': 'var(--color-red-700)',
                '--color-disabled-background': 'var(--color-gray-100)',
                '--color-disabled-text': 'var(--color-gray-400)',
                '--color-disabled-ring': 'var(--color-gray-700)',
                '--color-disabled-separator': 'var(--color-gray-500)',
                '--color-disabled-outline': 'var(--color-gray-500)',
            };

        case 'Gray':
            return {
                '--color-background': 'var(--color-gray-50)',
                '--color-background-active': 'var(--color-gray-100)',
                '--color-text': 'var(--color-gray-700)',
                '--color-ring': 'var(--color-gray-700)',
                '--color-ring-active': 'var(--color-gray-900)',
                '--color-outline': 'var(--color-gray-700)',
                '--color-separator': 'var(--color-gray-600)',
                '--color-separator-active': 'var(--color-gray-700)',
                '--color-error': 'var(--color-red-700)',
                '--color-disabled-background': 'var(--color-gray-100)',
                '--color-disabled-text': 'var(--color-gray-400)',
                '--color-disabled-ring': 'var(--color-gray-700)',
                '--color-disabled-separator': 'var(--color-gray-500)',
                '--color-disabled-outline': 'var(--color-gray-500)',
            };

        case 'Purple':
            return {
                '--color-background': 'var(--color-purple-50)',
                '--color-background-active': 'var(--color-purple-100)',
                '--color-text': 'var(--color-purple-700)',
                '--color-ring': 'var(--color-purple-700)',
                '--color-ring-active': 'var(--color-purple-900)',
                '--color-outline': 'var(--color-purple-700)',
                '--color-separator': 'var(--color-purple-600)',
                '--color-separator-active': 'var(--color-purple-700)',
                '--color-error': 'var(--color-red-700)',
                '--color-disabled-background': 'var(--color-gray-100)',
                '--color-disabled-text': 'var(--color-gray-400)',
                '--color-disabled-ring': 'var(--color-gray-700)',
                '--color-disabled-separator': 'var(--color-gray-500)',
                '--color-disabled-outline': 'var(--color-gray-500)',
            };

        default:
            throw new Error(`Unknown BadgeDropDown variant: ${props.currentValue.variant}`);
    }
});

const stateClasses = computed(() => {
    if (isDisabled.value) {
        return 'ring-(--color-ring)/10';
    }

    if (isRetry.value) {
        return 'ring-(--color-error) active:bg-(--color-background-active) outline outline-offset-0 outline-1 outline-(--color-error)';
    }

    if (isHovered.value) {
        return 'ring-(--color-ring) active:bg-(--color-background-active) active:ring-(--color-ring-active)';
    }

    return 'ring-(--color-ring)/10 active:bg-(--color-background-active) active:ring-(--color-ring-active)';
});

const focusClasses = computed(() => {
    if (isDisabled.value) {
        return 'focus-visible:outline-(--color-disabled-outline)';
    }

    if (isRetry.value) {
        return 'focus-visible:outline-(--color-error)';
    }

    return 'focus-visible:outline-(--color-outline)';
});

const separatorColorClasses = computed(() => {
    if (isDisabled.value) {
        return 'bg-(--color-disabled-separator)/20';
    }

    return 'bg-(--color-separator)/20 group-active/badge-dropdown:bg-(--color-separator-active)/20';
});

const sizeClasses = computed(() => {
    switch (props.size) {
        case 'Small-24':
            return 'text-xs leading-[1.5rem] py-0 pr-2 pl-2';

        case 'Medium-32':
            return 'text-base leading-[1.625rem] py-[0.1875rem] pr-2 pl-2.5';

        case 'Large-40':
            return 'text-base leading-[1.625rem] py-[0.4375rem] pr-2 pl-2.5';

        default:
            throw new Error(`Unknown BadgeDropDown size: ${props.size}`);
    }
});

const iconSizeClass = computed(() => {
    switch (props.size) {
        case 'Small-24':
            return 'size-4';

        case 'Medium-32':
            return 'size-4.5';

        case 'Large-40':
            return 'size-5';

        default:
            throw new Error(`Unknown BadgeDropDown size: ${props.size}`);
    }
});

const separatorHeightClass = computed(() => {
    switch (props.size) {
        case 'Small-24':
            return 'h-3.5';

        case 'Medium-32':
            return 'h-4.5';

        case 'Large-40':
            return 'h-5.5';

        default:
            throw new Error(`Unknown BadgeDropDown size: ${props.size}`);
    }
});

const currentValueRef = ref(null);
const valuesPanelRef = ref(null);
const wrapperRef = ref(null);
const { floatingStyles } = useFloating(currentValueRef, valuesPanelRef, {
    open: isOpen.value,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [
        offset(6), // number = Shorthand for main-axis
        flip({
            fallbackAxisSideDirection: 'start', // Which side to try first if neither the preferred option nor the fallback option fits
            crossAxis: true, // Flip to opposite axis when possible
        }),
        shift({ padding: 4 }), // padding = Minimal distance from tooltip to viewport edge
    ],
});

onClickOutside(wrapperRef, () => (isOpen.value = false));

// This is used to force close the Tooltip after clicking the button
const triggerHideTooltipKeyCounter = ref(0);
provide(triggerHideTooltipKey, triggerHideTooltipKeyCounter);

function triggerHideTooltip() {
    triggerHideTooltipKeyCounter.value = triggerHideTooltipKeyCounter.value + 1;
}

function keydownTrigger(event: KeyboardEvent) {
    if (canBeOpened.value) {
        handleKeyDown(event);
    } else if (event.key === ' ' || event.key === 'Enter') {
        emit('retry');
    }
}

function clickTrigger() {
    if (canBeOpened.value) {
        triggerHideTooltip();
        isOpen.value = !isOpen.value;
    }

    if (isRetry.value) {
        emit('retry');
    }
}
</script>
